import { LocationData, ActiveContract } from '../types';

/**
 * Progressive-disclosure signals for the Management panel. Shared by DatacenterView
 * (which dims/locks tools) and App (which fires a one-time "unlocked" cue when a
 * signal first flips true) so the two never drift.
 */
export interface UnlockSignals {
  hasAnyServer: boolean;      // first server installed
  isEarning: boolean;         // datacenter is connected / earning
  hasSecondLocation: boolean; // owns a 2nd datacenter
}

export const computeUnlockSignals = (
  locations: Record<string, LocationData>,
  activeContracts?: ActiveContract[],
): UnlockSignals => {
  const locs = Object.values(locations ?? {});
  const hasAnyServer = locs.some(loc =>
    Object.values(loc.floors ?? {}).some(f => (f.racks ?? []).some(r => (r.servers ?? []).length > 0)));
  const hasConnectivity = locs.some(loc =>
    loc.mmr?.built && (((loc.mmr.transitProviders ?? []).length > 0) || ((loc.mmr.ixpConnections ?? []).length > 0)));
  const unlockedCount = locs.filter(l => l.unlocked).length;
  return {
    hasAnyServer,
    isEarning: hasConnectivity || (activeContracts?.length ?? 0) > 0,
    hasSecondLocation: unlockedCount >= 2,
  };
};

/** Tools that become available at each unlock step — used for the "unlocked" toast. */
export const UNLOCK_TOOL_LABELS: Record<keyof UnlockSignals, string[]> = {
  hasAnyServer: ['Floor Quality', 'Staff', 'Fleet Overview', 'Financials', 'Upgrades', 'Goals'],
  isEarning: ['Contracts', 'Green Energy', 'Site Layout', 'Loans'],
  hasSecondLocation: ['Backbone'],
};
